import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, from } from 'rxjs';
import { OwnerForm } from 'src/app/classes/form-groups/owner-form';
import { Owner, Vendor } from 'src/app/classes/models';
import { AddEditOwnerModalComponent } from 'src/app/modals/add-edit-owner-modal/add-edit-owner-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { OwnerService } from 'src/app/services/owner.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { VendorService } from 'src/app/services/vendor.service';

@Component({
  selector: 'app-admin-vendor-owners',
  templateUrl: './admin-vendor-owners.component.html',
  styleUrls: ['./admin-vendor-owners.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminVendorOwnersComponent {
  constructor(
    private ToastService: ToastService,
    private VendorService: VendorService,
    private SharedDataService: SharedDataService,
    private ModalService: NgbModal,
    private OwnerService: OwnerService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {
    this.SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent<number>().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetVendorId' && typeof data === 'number') {
            this.VendorId = data;

            this.VendorService.getVendorV2(this.VendorId, {
              attributes: JSON.stringify(['id', 'legalName', 'country']),
              Owner: JSON.stringify({
                Address: {}
              })
            }).subscribe({
              next: (Vendor) => {
                this.Vendor = Vendor;
                const { legalName, Owners } = Vendor;

                if (!legalName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Vendor is missing legal name.',
                    { returnError: true }
                  );
                }

                if (Owners) {
                  Owners.forEach((Owner) => {
                    const OwnerFormGroup = new OwnerForm();
                    OwnerFormGroup.patchValue(Owner);
                    this.OwnerFormGroups.push(OwnerFormGroup);
                  });
                }

                this.SharedDataService.sendEvent(
                  'SetTitle',
                  `Owners - ${legalName}`
                );

                this.ChangeDetectorRef.markForCheck();
              }
            });
          }
        }
      });

    this.SharedDataService.sendEvent('GetVendorId');
  }

  VendorId?: number;
  SharedDataServiceSubscription?: Subscription;
  Vendor?: Vendor;
  OwnerFormGroups: OwnerForm[] = [];

  deleteOwner(OwnerFormGroup: OwnerForm, index: number) {
    const { id: OwnerId, VendorId } = OwnerFormGroup.getRawValue();

    if (!OwnerId) {
      throw this.ToastService.addErrorToast('Error', 'Owner is missing id.', {
        returnError: true
      });
    }

    if (!VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Owner is missing Vendor Id.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', size: 'md' }
    );

    Object.assign(componentInstance, {
      displayText:
        'Deleting an owner is permanent. Are you sure you want to proceed?'
    });

    from(result).subscribe({
      next: (response) => {
        if (response) {
          this.OwnerService.deleteOwner(OwnerId, VendorId).subscribe({
            next: () => {
              this.OwnerFormGroups.splice(index, 1);
              this.ChangeDetectorRef.markForCheck();
              throw this.ToastService.addSuccessToast(
                'Error',
                'Owner was successfully deleted.'
              );
            }
          });
        }
      }
    });
  }

  editOwner(OwnerFormGroup: OwnerForm) {
    if (!this.Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor information.',
        { returnError: true }
      );
    }

    const { id: VendorId, country } = this.Vendor;

    if (!country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing country information.',
        { returnError: true }
      );
    }

    const { value: Owner } = OwnerFormGroup;

    const { result, componentInstance } = this.ModalService.open(
      AddEditOwnerModalComponent,
      { backdrop: 'static', modalDialogClass: 'add-edit-owner-modal' }
    );

    Object.assign(componentInstance, { Owner, VendorId, country });

    from(result).subscribe({
      next: (SavedOwner: Owner) => {
        OwnerFormGroup.patchValue(SavedOwner);
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  addOwner() {
    if (!this.Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor information.',
        { returnError: true }
      );
    }

    const { id: VendorId, country } = this.Vendor;

    if (!VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Vendor is missing id.', {
        returnError: true
      });
    }

    if (!country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing country information.',
        { returnError: true }
      );
    }

    const { result, componentInstance } = this.ModalService.open(
      AddEditOwnerModalComponent,
      { backdrop: 'static', modalDialogClass: 'add-edit-owner-modal' }
    );

    Object.assign(componentInstance, {
      Owner: { VendorId },
      VendorId,
      country
    });

    from(result).subscribe({
      next: (SavedOwner: Owner) => {
        const OwnerFormGroup = new OwnerForm();
        OwnerFormGroup.patchValue(SavedOwner);
        this.OwnerFormGroups.push(OwnerFormGroup);
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.SharedDataServiceSubscription) {
      this.SharedDataServiceSubscription.unsubscribe();
    }
  }
}
